import React from 'react';
import { Table } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import styles from './MeterReadDetailsTable.module.css';
import MeterReadDetailsTableMonthCell from './MeterReadDetailsTableMonthCell';
import MeterReadDetailsFileCell from './MeterReadDetailsFileCell';
import { toTestAttrValue, type TestAttrs } from '@utils/testAttr';


/** minimal types so TS is happy */
type Cell = {
  month_year: string;
  value: number | string | null;
  meter_computed_id?: number;
  readonly?: boolean;
  isEffectiveMonth?: boolean;
  isApproved?: boolean;
};
type Row = {
  field: string;
  label: string;
  months: Cell[];
  editable?: boolean;
  retroInput?: boolean;
  collapsed?: boolean;
  childrenCollapsed?: boolean;
  separatorBelow?: boolean;
  sectionStart?: boolean;
  subRow?: boolean;
  onToggleCollapse?: () => void;
};

const monthLabels = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface MeterReadDetailsTableProps {
  id: string | undefined;
  rows: Row[];
  handleChange: any;
  saving: any;
  updating: any;
  additionalColumns?: ColumnType<Row>[];
  additionalStyles?: string;
  start?: Date | string;
  end?: Date | string;
  shouldUseContractView?: boolean;
  isEditable: boolean;
  noDecimals?: boolean;
  /** Playwright data-table value telling the page's tables apart, e.g. "main", "meter-swap" */
  testTable?: string;
}

const MeterReadDetailsTable = ({
  id,
  rows,
  handleChange,
  saving,
  updating,
  additionalColumns,
  additionalStyles,
  start,
  end,
  shouldUseContractView,
  isEditable,
  noDecimals,
  testTable,
}: MeterReadDetailsTableProps) => {
  /** Column widths chosen so sum > 1200px -> horizontal scrollbar appears */
  const FIELD_W = 250;
  const MONTH_W = 150;

  // Parse date strings (YYYY-MM-DD) to Date objects in local timezone
  const parseDate = (dateStr: string | Date | undefined) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day || 1);
  };

  const startDate = parseDate(start);

  const monthIndices = Array.from({ length: 12 }, (_, index) => {
    if (!shouldUseContractView || !startDate) {
      return index; // Normal calendar year view: 0-11
    }

    // Contract mode: calculate which calendar month should be shown in each column
    // Column 0 = contract start month, Column 1 = next month, etc.
    const contractMonthIndex = (startDate.getMonth() + index) % 12;

    // If contract wraps to next year and we're in the wrapped portion,
    // we need to check if data exists for that month
    // For now, just return the month index - the render will handle missing data
    return contractMonthIndex;
  });

  const fieldCol: ColumnType<Row> = {
    title: 'Field',
    dataIndex: 'label',
    fixed: 'left' as const,
    width: FIELD_W,
    onHeaderCell: () => ({
      className: styles.wrapHeader,
      style: { minWidth: FIELD_W },
      'data-testid': 'column-header',
      'data-column': 'field',
    }) as TestAttrs,
    onCell: () => ({ className: styles.fieldCell, style: { minWidth: FIELD_W }, 'data-testid': 'field-label' }) as TestAttrs,
    render: (text: string, record: Row) => {
      const labelClass = record.retroInput
        ? `${styles.fieldCell} ${styles.retroInputLabel}`
        : styles.fieldCell;
      if (record.onToggleCollapse !== undefined) {
        return (
          <div className={`${labelClass} ${styles.collapsibleLabel}`}>
            {text}
            <button
              className={styles.collapseToggle}
              onClick={record.onToggleCollapse}
              data-testid="row-expand-toggle"
              data-expanded={String(!record.childrenCollapsed)}
              aria-expanded={!record.childrenCollapsed}
              aria-label={record.childrenCollapsed ? `Expand ${text}` : `Collapse ${text}`}
            >
              {record.childrenCollapsed ? '▶' : '▼'}
            </button>
          </div>
        );
      }
      return <div className={labelClass}>{text}</div>;
    },
  };

  const monthCols: ColumnType<Row>[] = monthIndices.map(
    (monthIndex: number, columnIndex: number): ColumnType<Row> => ({
      title: <span className={styles.monthHeader}>{monthLabels[monthIndex]}</span>,
      dataIndex: `month-${columnIndex}`,
      width: MONTH_W,
      align: 'right' as const,
      onHeaderCell: () => ({
        className: styles.wrapHeader,
        'data-testid': 'column-header',
        'data-column': monthLabels[monthIndex].toLowerCase(),
      }) as TestAttrs,
      onCell: (record: Row) => ({
        'data-testid': 'month-value',
        'data-column': monthLabels[monthIndex].toLowerCase(),
        'data-month-year': record.months[columnIndex]?.month_year ?? '',
      }) as TestAttrs,
      render: (_: unknown, record: Row) => {
        if (record.collapsed) return null;
        // Use columnIndex to access the sequential months array from backend
        const cell = record.months[columnIndex];

        if (record.field === 'file_name') {
          return (
            <MeterReadDetailsFileCell
              id={id}
              data={cell}
              styles={styles}
            />
          );
        }

        return (
          <MeterReadDetailsTableMonthCell
            id={id}
            editable={!!record.editable}
            isEditable={isEditable}
            data={cell}
            type={(record as any).type}
            saving={saving}
            updating={updating}
            styles={styles}
            label={record.label}
            noDecimals={noDecimals}
            retroInput={!!record.retroInput}
            onChange={(value) => handleChange(record.field, columnIndex, value, cell)}
          />
        );
      },
    })
  );

  // Totals columns (Running Total, Contract Total, Energy Bid): data-column is the kebab-cased dataIndex
  const totalCols: ColumnType<Row>[] = (additionalColumns ?? []).map((col) => {
    const column = toTestAttrValue(String(col.dataIndex ?? '').replace(/([a-z])([A-Z])/g, '$1-$2'));
    return {
      ...col,
      onHeaderCell: (c) => ({ ...col.onHeaderCell?.(c), 'data-testid': 'column-header', 'data-column': column }) as TestAttrs,
      onCell: (record, index) => ({ ...col.onCell?.(record, index), 'data-testid': 'total-value', 'data-column': column }) as TestAttrs,
    };
  });

  let columns: ColumnsType<Row> = [fieldCol, ...monthCols, ...totalCols];

  // Calculate scroll width for proper fixed column behavior
  const additionalColsWidth = (additionalColumns ?? []).reduce(
    (sum, col) => sum + (typeof col.width === 'number' ? col.width : 0),
    0
  );
  const scrollX = FIELD_W + MONTH_W * 12 + additionalColsWidth;

  return (
    <div className={styles.container}>
      {/* horizontal scroll wrapper */}
      <div className={styles.hScroll}>
        <Table<Row>
          columns={columns}
          dataSource={rows}
          rowKey="field"
          pagination={false}
          scroll={{ x: scrollX }}
          tableLayout="fixed"
          size="small"
          bordered
          className={`${styles.meterReadTable} ${additionalStyles ?? ''}`}
          data-testid="meter-read-details-table"
          data-table={testTable}
          onRow={(record) => ({
            'data-testid': 'meter-read-row',
            'data-field': record.field,
            ...(record.onToggleCollapse !== undefined && { 'data-expanded': String(!record.childrenCollapsed) }),
            ...(record.subRow && { 'data-sub-row': 'true' }),
          }) as TestAttrs}
          rowClassName={(record) => {
            const classes: string[] = [];
            if (record.collapsed) classes.push(styles.collapsedRow);
            if (record.separatorBelow) classes.push(styles.separatorRow);
            if (record.sectionStart) classes.push(styles.sectionStartRow);
            if (record.subRow) classes.push(styles.subRow);
            return classes.join(' ');
          }}
        />
      </div>
    </div>
  );
};

export default MeterReadDetailsTable;